import {Caption, Word} from '@contentlab/types/captions';
import {TEXT_PROCESSING} from '../config/CaptionRenderingConfig';
import {getTimedWords} from './WordTimingProcessor';
import {CaptionDivisionParams} from './types';

interface ShouldStartNewCaptionParams {
  currentWords: Word[];
  word: Word;
  currentText: string;
  currentLine: string;
  currentLineCount: number;
  currentStartTime: number;
  context: CanvasRenderingContext2D;
  maxWidth: number;
}

export const getCurrentCaption = (
  captions: Caption[],
  currentTime: number
): Caption | undefined => {
  const active = captions.filter(
    (c) => currentTime >= c.start && currentTime < c.end
  );
  return active.sort((a, b) => b.start - a.start)[0];
};

const exceedsLineWidth = (
  lineText: string,
  wordText: string,
  context: CanvasRenderingContext2D,
  maxWidth: number
): boolean => {
  const testLine = lineText ? `${lineText} ${wordText}` : wordText;
  return context.measureText(testLine).width > maxWidth;
};

const shouldStartNewCaption = ({
  currentWords,
  word,
  currentText,
  currentLine,
  currentLineCount,
  currentStartTime,
  context,
  maxWidth,
}: ShouldStartNewCaptionParams): boolean => {
  const {
    MIN_CAPTION_DURATION_SECONDS,
    LINE_LIMIT,
    CHAR_LIMIT,
    HARD_LINE_LIMIT,
    HARD_CHAR_LIMIT,
  } = TEXT_PROCESSING;

  // If no current words, never start new caption
  if (currentWords.length === 0) {
    return false;
  }

  const potentialText = currentText ? `${currentText} ${word.text}` : word.text;
  const wouldExceedCharLimit = potentialText.length > CHAR_LIMIT;
  const wouldExceedHardCharLimit = potentialText.length > HARD_CHAR_LIMIT;

  const wouldNeedNewLine = exceedsLineWidth(
    currentLine,
    word.text,
    context,
    maxWidth
  );
  const wouldExceedLineLimit =
    wouldNeedNewLine && currentLineCount >= LINE_LIMIT;
  const wouldExceedHardLineLimit =
    wouldNeedNewLine && currentLineCount >= HARD_LINE_LIMIT;
  const currentDuration = word.start - currentStartTime;
  const hasMinimumDuration = currentDuration >= MIN_CAPTION_DURATION_SECONDS;

  // Hard limits always trigger new caption
  if (wouldExceedHardCharLimit || wouldExceedHardLineLimit) {
    return true;
  }

  // Soft limits only trigger if minimum duration is met
  if (hasMinimumDuration && (wouldExceedCharLimit || wouldExceedLineLimit)) {
    return true;
  }

  return false;
};

export const divideCaption = ({
  caption,
  context,
  maxWidth,
}: CaptionDivisionParams): Caption[] => {
  const timedWords = getTimedWords(caption);

  if (!timedWords.length) return [caption];

  const dividedCaptions: Caption[] = [];
  let currentWords: Word[] = [];
  let currentText = '';
  let currentLineCount = 1;
  let currentLine = '';
  let currentStartTime = timedWords[0].start;

  const saveCaption = (endTime: number) => {
    if (currentWords.length === 0) return;
    dividedCaptions.push({
      id: `${caption.id}_${dividedCaptions.length}`,
      text: currentText,
      start: currentStartTime,
      end: endTime,
      wordTimings: [...currentWords],
    });
  };

  timedWords.forEach((word) => {
    const shouldStartNew = shouldStartNewCaption({
      currentWords,
      word,
      currentText,
      currentLine,
      currentLineCount,
      currentStartTime,
      context,
      maxWidth,
    });

    if (shouldStartNew) {
      saveCaption(word.start);
      currentWords = [word];
      currentText = word.text;
      currentLineCount = 1;
      currentLine = word.text;
      currentStartTime = word.start;
    } else {
      const potentialText = currentText
        ? `${currentText} ${word.text}`
        : word.text;

      const wouldNeedNewLine = exceedsLineWidth(
        currentLine,
        word.text,
        context,
        maxWidth
      );

      if (wouldNeedNewLine && currentWords.length > 0) {
        currentLineCount += 1;
        currentLine = word.text;
      } else {
        currentLine = currentLine ? `${currentLine} ${word.text}` : word.text;
      }
      currentWords.push(word);
      currentText = potentialText;
    }
  });

  if (currentWords.length > 0) {
    saveCaption(caption.end);
  }
  return dividedCaptions;
};
